import {createElement} from "@opendaw/lib-jsx"

import {isDefined, Optional} from "@opendaw/lib-std"
import {Colors, IconSymbol} from "@opendaw/studio-enums"
import {DeviceLatency, MicSource} from "@opendaw/studio-core"
import {Dialog} from "@/ui/components/Dialog.tsx"
import {Surface} from "@/ui/surface/Surface.tsx"

type Step =
    | {type: "idle"}
    | {type: "measuring", attempt: number}
    | {type: "done", ms: number, deviceId: string}
    | {type: "error", message: string}

const ATTEMPTS = 3
const CLICK_FREQ = 1000
const CLICK_DURATION = 0.02
const TIMEOUT_MS = 2500

const micConstraints = (deviceId: Optional<string>): MediaStreamConstraints => ({
    audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        ...(isDefined(deviceId) ? {deviceId: {exact: deviceId}} : {})
    }
})

async function measureOnce(audioContext: AudioContext, signal: AbortSignal, deviceId: Optional<string>): Promise<number> {
    const stream = await navigator.mediaDevices.getUserMedia(micConstraints(deviceId))
    if (signal.aborted) {
        stream.getTracks().forEach(t => t.stop())
        throw new DOMException("Aborted", "AbortError")
    }
    const source = audioContext.createMediaStreamSource(stream)
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 512
    source.connect(analyser)
    const data = new Float32Array(analyser.fftSize)

    try {
        // Measure background noise over ~150ms
        await new Promise(r => setTimeout(r, 150))
        analyser.getFloatTimeDomainData(data)
        let bgPeak = 0
        for (const v of data) {bgPeak = Math.max(bgPeak, Math.abs(v))}
        const threshold = Math.max(bgPeak * 5, 0.04)

        // Build a short sine-burst click
        const clickLen = Math.ceil(audioContext.sampleRate * CLICK_DURATION)
        const clickBuf = audioContext.createBuffer(1, clickLen, audioContext.sampleRate)
        const ch = clickBuf.getChannelData(0)
        for (let i = 0; i < clickLen; i++) {
            const env = 1 - i / clickLen
            ch[i] = Math.sin(i * 2 * Math.PI * CLICK_FREQ / audioContext.sampleRate) * env
        }
        const clickNode = audioContext.createBufferSource()
        clickNode.buffer = clickBuf
        clickNode.connect(audioContext.destination)
        const t0 = audioContext.currentTime + 0.05
        clickNode.start(t0)

        return await new Promise<number>((resolve, reject) => {
            const deadline = performance.now() + TIMEOUT_MS
            const minPollTime = t0 * 1000 + 60

            const poll = () => {
                if (signal.aborted) {reject(new DOMException("Aborted", "AbortError")); return}
                if (performance.now() > deadline) {reject(new Error("no-echo")); return}
                if (performance.now() < minPollTime) {requestAnimationFrame(poll); return}

                analyser.getFloatTimeDomainData(data)
                let peak = 0
                for (const v of data) {peak = Math.max(peak, Math.abs(v))}
                if (peak > threshold) {
                    const roundTripMs = (audioContext.currentTime - t0) * 1000
                    resolve(Math.max(0, Math.round(roundTripMs - audioContext.outputLatency * 1000)))
                } else {
                    requestAnimationFrame(poll)
                }
            }
            requestAnimationFrame(poll)
        })
    } finally {
        source.disconnect()
        stream.getTracks().forEach(t => t.stop())
    }
}

async function runCalibration(audioContext: AudioContext, signal: AbortSignal): Promise<{ms: number, deviceId: string}> {
    const requestedDeviceId = MicSource.deviceId.unwrapOrUndefined()
    const probe = await navigator.mediaDevices.getUserMedia(micConstraints(requestedDeviceId))
    const deviceId = probe.getAudioTracks().at(0)?.getSettings().deviceId ?? requestedDeviceId ?? ""
    probe.getTracks().forEach(t => t.stop())

    const results: number[] = []
    for (let i = 0; i < ATTEMPTS; i++) {
        if (signal.aborted) {throw new DOMException("Aborted", "AbortError")}
        const ms = await measureOnce(audioContext, signal, requestedDeviceId)
        results.push(ms)
        if (i < ATTEMPTS - 1) {await new Promise(r => setTimeout(r, 300))}
    }
    results.sort((a, b) => a - b)
    return {ms: results[Math.floor(results.length / 2)], deviceId}
}

export const showAudioCalibrationModal = (audioContext: AudioContext, origin?: Element, onClose?: () => void): void => {
    const abort = new AbortController()
    type Btn = {text: string, primary: boolean, onClick: (h: {close(): void}) => void}
    let step: Step = {type: "idle"}

    const body: HTMLDivElement = <div style={{padding: "1em 0", minWidth: "22rem"}}/>
    const closeBtn: Btn = {
        text: "Close",
        primary: false,
        onClick: ({close}) => {abort.abort(); close()}
    }
    const measureBtn: Btn = {
        text: "Measure",
        primary: true,
        onClick: () => startMeasurement()
    }

    const dialog: HTMLDialogElement = (
        <Dialog headline="Calibrate audio latency"
                icon={IconSymbol.Microphone}
                cancelable={true}
                onCancel={() => abort.abort()}
                buttons={[]}>
            {body}
        </Dialog>
    )

    const render = () => {
        while (body.firstChild) {body.removeChild(body.firstChild)}
        const p = (text: string, color?: string): HTMLParagraphElement => {
            const el: HTMLParagraphElement = <p style={{margin: "0 0 0.5em 0", color: color ?? ""}}>{text}</p>
            return el
        }
        switch (step.type) {
            case "idle":
                body.append(
                    p(`Calibrating: ${MicSource.label()}`),
                    p("This plays a short click through your speakers and listens for the echo on your microphone."),
                    p("Make sure your microphone is near your speakers (built-in laptop mic/speaker works well).", Colors.shadow.toString()),
                    p("Headphones with a separate microphone will not work for this measurement.", Colors.shadow.toString())
                )
                setFooterButtons([closeBtn, measureBtn])
                break
            case "measuring": {
                const status: HTMLParagraphElement = <p style={{margin: "0", fontWeight: "bold"}}>
                    {`Measuring… attempt ${step.attempt} of ${ATTEMPTS}`}
                </p>
                body.append(status)
                setFooterButtons([{text: "Cancel", primary: false, onClick: ({close}: {close(): void}) => {abort.abort(); close()}}])
                break
            }
            case "done": {
                body.append(
                    p(`Measured input latency: ${step.ms} ms`),
                    p(`Saved for this device. Future recordings will be automatically aligned.`, Colors.shadow.toString())
                )
                setFooterButtons([{text: "Close", primary: true, onClick: ({close}: {close(): void}) => close()}])
                break
            }
            case "error":
                body.append(
                    p(step.message, Colors.red.toString()),
                    p("Make sure your microphone can hear the speakers and try again.", Colors.shadow.toString())
                )
                setFooterButtons([closeBtn, {...measureBtn, text: "Try again"} as Btn])
                break
        }
    }

    const footer = () => dialog.querySelector("footer") as HTMLElement
    const setFooterButtons = (buttons: ReadonlyArray<Btn>) => {
        const f = footer()
        while (f.firstChild) {f.removeChild(f.firstChild)}
        for (const btn of buttons) {
            const el: HTMLButtonElement = (
                <button style={{
                    padding: "0.4em 1em",
                    cursor: "pointer",
                    background: btn.primary ? Colors.blue.toString() : "transparent",
                    color: btn.primary ? "white" : Colors.gray.toString(),
                    border: `1px solid ${btn.primary ? Colors.blue : Colors.gray}`,
                    borderRadius: "4px",
                    marginLeft: "0.5em"
                }}
                onclick={() => btn.onClick({close: () => dialog.close()})}>
                    {btn.text}
                </button>
            )
            f.appendChild(el)
        }
    }

    const startMeasurement = async () => {
        for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
            step = {type: "measuring", attempt}
            render()
            if (attempt > 1) {await new Promise(r => setTimeout(r, 300))}
        }
        step = {type: "measuring", attempt: 1}
        render()
        try {
            if (audioContext.state === "suspended") {await audioContext.resume()}
            const {ms, deviceId} = await runCalibration(audioContext, abort.signal)
            DeviceLatency.save(deviceId, ms)
            step = {type: "done", ms, deviceId}
        } catch (err) {
            if (!abort.signal.aborted) {
                const isNoEcho = err instanceof Error && err.message === "no-echo"
                step = {
                    type: "error",
                    message: isNoEcho
                        ? "No echo detected — the microphone could not hear the click."
                        : `Measurement failed: ${err instanceof Error ? err.message : String(err)}`
                }
            }
        }
        if (!abort.signal.aborted) {render()}
    }

    render()
    if (isDefined(onClose)) {dialog.addEventListener("close", () => onClose())}
    Surface.get(origin).body.appendChild(dialog)
    dialog.showModal()
}
